import { type HierarchyCircularNode, hierarchy, pack } from 'd3-hierarchy';
import { truncateLabel } from '../graph';
import { plural } from '../review-inbox';
import { PRINT_COLORS } from '../snapshots';
import { attrId, FIGURE_WIDTH, n1, svgFrame, svgLabel } from './svg';

// The clusters as a print figure (full reports), drawn like the Clusters screen: each cluster a circle in its palette
// color holding its member bubbles, sized by links (the sum of their link strengths). The report's own entities are
// outlined; the others are lighter. Each cluster is labeled with its name and its three most linked members, and a
// legend names every cluster. Pure and deterministic.

export const CLUSTERS_SVG_TOP_MEMBERS = 3;
const PACK_H = 520;
const PAD = 20;
const LEGEND_ROW = 18;
/** Room above the topmost circle for a cluster name drawn above it. */
const NAME_ROOM = 20;

export interface ClustersSvgMember {
  id: string;
  displayName: string;
  /** Link strength inside the cluster. */
  degree: number;
  reviewStatus: string;
}

export interface ClustersSvgCluster {
  id: string;
  label: string | null;
  colorSlot: number | null;
  /** All members, also those not drawn. */
  size: number;
  members: ClustersSvgMember[];
}

export interface ClustersSvgInput {
  clusters: ClustersSvgCluster[];
  /** Entities to outline (the report's results); empty = none. */
  highlight: ReadonlySet<string>;
  title: string;
}

interface Leaf {
  clusterId?: string;
  member?: ClustersSvgMember;
  value?: number;
  children?: Leaf[];
}

const byDegree = (a: ClustersSvgMember, b: ClustersSvgMember) => b.degree - a.degree || a.id.localeCompare(b.id);
const clusterName = (c: ClustersSvgCluster) => c.label ?? 'Unnamed cluster';

export function clustersSvg(input: ClustersSvgInput): { svg: string; width: number; height: number } {
  const { highlight, title } = input;
  const width = FIGURE_WIDTH;
  const clusters = [...input.clusters].filter(c => c.members.length).sort((a, b) => b.size - a.size || a.id.localeCompare(b.id));
  const marked = highlight.size > 0;
  if (!clusters.length) {
    const h = 2 * PAD + 40;
    return { svg: svgFrame(width, h, title, svgLabel('No clusters to draw.', PAD, h / 2, { fill: PRINT_COLORS.muted })), width, height: h };
  }

  const tree: Leaf = {
    children: clusters.map(c => ({
      clusterId: c.id,
      children: [...c.members].sort(byDegree).map(m => ({ member: m, value: Math.max(0.5, m.degree) })),
    })),
  };
  const key = (d: Leaf) => d.clusterId ?? d.member?.id ?? '';
  const root = pack<Leaf>()
    .size([width - 2 * PAD, PACK_H])
    .padding(d => (d.depth === 0 ? 14 : 3))(
    hierarchy<Leaf>(tree)
      .sum(d => d.value ?? 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0) || key(a.data).localeCompare(key(b.data))),
  );
  const nodes = (root.children ?? []) as HierarchyCircularNode<Leaf>[];
  // The packed circles rarely fill the box: crop to them, keeping room for a name above the topmost circle.
  const minY = Math.min(...nodes.map(n => n.y - n.r));
  const maxY = Math.max(...nodes.map(n => n.y + n.r));
  const dy = PAD + NAME_ROOM - minY;
  const legendTop = PAD + NAME_ROOM + (maxY - minY) + PAD;
  const legendRows = clusters.length + (marked ? 1 : 0);
  const height = Math.round(legendTop + legendRows * LEGEND_ROW + PAD);
  const byId = new Map(clusters.map(c => [c.id, c]));
  const topOf = (c: ClustersSvgCluster) => [...c.members].sort(byDegree).slice(0, CLUSTERS_SVG_TOP_MEMBERS);

  const circles: string[] = [];
  const labels: string[] = [];
  for (const node of nodes) {
    const c = byId.get(node.data.clusterId ?? '');
    if (!c) continue;
    const color = PRINT_COLORS.entity(c.colorSlot);
    const cx = PAD + node.x;
    const cy = dy + node.y;
    circles.push(
      `<circle cx="${n1(cx)}" cy="${n1(cy)}" r="${n1(node.r)}" fill="${color}" fill-opacity="0.1" stroke="${color}" stroke-width="2" data-cluster-id="${attrId(c.id)}"/>`,
    );
    const top = new Set(topOf(c).map(m => m.id));
    for (const leaf of node.children ?? []) {
      const m = leaf.data.member;
      if (!m) continue;
      const hit = highlight.has(m.id);
      const x = PAD + leaf.x;
      const y = dy + leaf.y;
      const r = Math.max(1.5, leaf.r);
      circles.push(
        `<circle cx="${n1(x)}" cy="${n1(y)}" r="${n1(r)}" fill="${color}" fill-opacity="${!marked || hit ? 0.9 : 0.4}" stroke="${
          hit ? PRINT_COLORS.foreground : PRINT_COLORS.background
        }" stroke-width="${hit ? 2 : 1}"${m.reviewStatus === 'unreviewed' ? ' stroke-dasharray="2 1.5"' : ''} data-entity-id="${attrId(m.id)}"${
          hit ? ' data-highlight="true"' : ''
        }/>`,
      );
      // On the bubble, cut to its width so neighbors don't collide; the legend gives the full names.
      if (top.has(m.id))
        labels.push(
          svgLabel(m.displayName, x, y, {
            size: 9,
            max: Math.max(4, Math.floor((2 * r) / 5.2)),
            halo: true,
            align: 'center',
            weight: hit ? 600 : undefined,
          }),
        );
    }
    // The name inside the top of a large circle, above a small one.
    const ly = node.r > 40 ? cy - node.r + 14 : cy - node.r - 8;
    labels.push(
      svgLabel(clusterName(c), cx, ly, { size: 12, weight: 600, max: Math.max(14, Math.floor(node.r / 3)), halo: true, align: 'center' }),
    );
  }

  // Legend: every cluster with its size and its most linked members.
  const legend: string[] = [];
  const rowY = (i: number) => legendTop + i * LEGEND_ROW + LEGEND_ROW / 2;
  clusters.forEach((c, i) => {
    const y = rowY(i);
    const color = PRINT_COLORS.entity(c.colorSlot);
    legend.push(
      `<circle cx="${n1(PAD + 6)}" cy="${n1(y)}" r="6" fill="${color}" fill-opacity="0.9" stroke="${PRINT_COLORS.foreground}" stroke-opacity="0.55"/>`,
      svgLabel(`${clusterName(c)} (${plural(c.size, 'member')})`, PAD + 18, y, { size: 10, weight: 600, max: 44 }),
      svgLabel(
        topOf(c)
          .map(m => truncateLabel(m.displayName, 24))
          .join(' · '),
        PAD + 300,
        y,
        { size: 10, fill: PRINT_COLORS.muted, max: 80 },
      ),
    );
  });
  if (marked) {
    const y = rowY(clusters.length);
    legend.push(
      `<circle cx="${n1(PAD + 6)}" cy="${n1(y)}" r="6" fill="${PRINT_COLORS.background}" stroke="${PRINT_COLORS.foreground}" stroke-width="2"/>`,
      svgLabel("Outlined: the report's results", PAD + 18, y, { size: 10, fill: PRINT_COLORS.muted, max: 52 }),
    );
  }

  const body = `<g>${circles.join('')}</g>\n<g>${labels.join('')}</g>\n<g>${legend.join('')}</g>`;
  return { svg: svgFrame(width, height, title, body), width, height };
}
